import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage } from "@fortawesome/free-solid-svg-icons";
import { chatListTimestamp } from "../../utils/timeFormat";
import { isOnline } from "./chatPresence";

// Last message in the thread, sender-agnostic, single line, ellipsis truncated.
// Truncation is CSS (`truncate`), not a substring — a character budget breaks at
// different places on every panel width.
function Preview({ chat }) {
  const last = chat?.last_message;
  if (last?.message) {
    return <span className="truncate">{last.message}</span>;
  }
  if (last?.attachments?.length > 0) {
    return (
      <span className="flex items-center gap-1.5 truncate">
        <FontAwesomeIcon icon={faImage} />
        Image
      </span>
    );
  }
  return <span className="truncate">No messages</span>;
}

// A row in the chat list — the same component on the desktop left panel and the
// mobile list.
//
// States:
//   default   white, no fill
//   hover     #F5F7F7, transient, cursor only
//   selected  deeper fill + 3px #004C50 left bar, persists while the thread is
//             open (desktop only — mobile pushes to a screen instead)
//   unread    handle bold + #004C50 dot at the far right; clears on open, and
//             the handle drops back to medium at the same time
// Hover can apply on top of unread; hover must never look like selected.
export default function ChatListRow({ chat, unread, selected, onOpen }) {
  const friend = chat?.friend_details;
  const online = isOnline(friend);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className={`relative flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors ${
        selected ? "bg-[#EDF1F1]" : "bg-white hover:bg-[#F5F7F7]"
      }`}
    >
      {selected && (
        <span
          aria-hidden="true"
          className="absolute inset-y-0 left-0 w-[3px] bg-primaryColor"
        />
      )}

      <div className="relative flex-shrink-0">
        <img
          src={friend?.image}
          alt=""
          className="h-10 w-10 rounded-full object-cover"
        />
        {/* Green = live presence, real data only. */}
        {online && (
          <span className="absolute bottom-0 right-0 block h-3 w-3 rounded-full border-2 border-white bg-presence" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span
            className={`truncate text-sm text-gray-900 ${
              unread ? "font-bold" : "font-medium"
            }`}
          >
            {friend?.user_name?.replace(/^@/, "")}
          </span>
          <span className="flex-shrink-0 text-xs text-secondary">
            {chatListTimestamp(chat?.last_message?.created_at)}
          </span>
        </div>
        <div className="mt-0.5 flex items-center justify-between gap-2">
          <p className="flex min-w-0 flex-1 text-sm text-secondary">
            <Preview chat={chat} />
          </p>
          {unread && (
            <span
              aria-label="Unread"
              className="h-2 w-2 flex-shrink-0 rounded-full bg-unread"
            />
          )}
        </div>
      </div>
    </div>
  );
}
